import { Button } from "@/components/ui/button";
import { ShoppingBag, Star, ArrowRight, Truck, CreditCard, ShieldCheck, Heart, Search, Tag } from "lucide-react";
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string; desc: string; product: string; city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const products = [
  { name: "Sac en cuir artisanal", price: "189 EUR", badge: "Nouveau", img: "bg-gradient-to-br from-amber-800/40 to-amber-600/20" },
  { name: "Montre classique", price: "249 EUR", badge: "Best-seller", img: "bg-gradient-to-br from-slate-600/40 to-slate-400/20" },
  { name: "Bijou fait main", price: "79 EUR", badge: null, img: "bg-gradient-to-br from-rose-600/40 to-rose-400/20" },
  { name: "Parfum d'exception", price: "120 EUR", badge: "Exclusif", img: "bg-gradient-to-br from-violet-600/40 to-violet-400/20" },
  { name: "Foulard en soie", price: "95 EUR", badge: null, img: "bg-gradient-to-br from-teal-600/40 to-teal-400/20" },
  { name: "Portefeuille cuir", price: "65 EUR", badge: "-20%", img: "bg-gradient-to-br from-orange-600/40 to-orange-400/20" },
];

const EcommerceTemplate = ({ name, desc, product, city, customizations, editMode = false, onCustomize }: Props) => {
  const c = customizations || {};
  const edit = (field: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(field, v);
  const displayName = c.heroTitle || name;
  const displayDesc = c.heroSubtitle || desc;
  const displayProduct = c.productName || product || "Des produits";
  const displayCta = c.ctaText || "Découvrir";
  return (
  <div className="bg-white text-gray-900 min-h-screen">
    {/* Nav */}
    <nav className="border-b border-gray-200 bg-white sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <EditableText as="span" value={displayName} onChange={edit("heroTitle")} editMode={editMode} className="text-xl font-bold text-emerald-600" data-field="brand-name" />
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
          <span className="hover:text-emerald-600 cursor-pointer">Nouveautés</span>
          <span className="hover:text-emerald-600 cursor-pointer">Collections</span>
          <span className="hover:text-emerald-600 cursor-pointer">Promotions</span>
          <span className="hover:text-emerald-600 cursor-pointer">Notre histoire</span>
        </div>
        <div className="flex items-center gap-4">
          <Search className="h-5 w-5 text-gray-500" />
          <Heart className="h-5 w-5 text-gray-500" />
          <div className="relative">
            <ShoppingBag className="h-5 w-5 text-gray-500" />
            <span className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-emerald-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">3</span>
          </div>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-20">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
            <Tag className="h-3 w-3" /> Nouvelle collection
          </span>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight text-gray-900">
            <EditableText as="span" value={displayProduct} onChange={edit("productName")} editMode={editMode} data-field="hero-title" /><br />
            <span className="text-emerald-600">d'exception</span>
          </h1>
          <EditableText as="p" value={displayDesc} onChange={edit("heroSubtitle")} editMode={editMode} multiline className="text-lg text-gray-600 max-w-md" data-field="hero-subtitle" />
          <div className="flex gap-4">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 rounded-full">
              <EditableText as="span" value={displayCta} onChange={edit("ctaText")} editMode={editMode} data-field="cta-primary" /> <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" className="h-12 px-8 rounded-full border-gray-300">Promotions</Button>
          </div>
        </div>
        <div className="hidden lg:grid grid-cols-2 gap-4">
          {products.slice(0, 4).map((p, i) => (
            <div key={i} className={`aspect-square rounded-2xl ${p.img} flex items-end p-4`}>
              <span className="text-xs font-medium text-gray-700 bg-white/80 px-2 py-1 rounded-full">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="border-y border-gray-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { icon: Truck, text: "Livraison gratuite dès 50 EUR" },
          { icon: ShieldCheck, text: "Paiement 100% sécurisé" },
          { icon: CreditCard, text: "3x sans frais" },
          { icon: Star, text: "4.8/5 (2 400+ avis)" },
        ].map((t) => (
          <div key={t.text} className="flex items-center gap-3">
            <t.icon className="h-5 w-5 text-emerald-600 shrink-0" />
            <span className="text-sm text-gray-600">{t.text}</span>
          </div>
        ))}
      </div>
    </section>

    {/* Products */}
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Boutique</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-1">Nos best-sellers</h2>
          </div>
          <Button variant="ghost" className="text-emerald-600 hover:text-emerald-700">Voir tout <ArrowRight className="ml-1 h-4 w-4" /></Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <div key={i} className="group cursor-pointer">
              <div className={`aspect-[4/5] rounded-2xl ${p.img} mb-4 relative overflow-hidden`}>
                {p.badge && <span className="absolute top-3 left-3 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{p.badge}</span>}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                  <Button className="bg-white text-gray-900 hover:bg-gray-100 rounded-full h-10 px-6 text-sm">Aperçu rapide</Button>
                </div>
              </div>
              <h3 className="font-medium text-gray-900 group-hover:text-emerald-600 transition-colors">{p.name}</h3>
              <p className="text-sm font-bold text-emerald-600 mt-1">{p.price}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Banner */}
    <section className="bg-emerald-600 py-16">
      <div className="max-w-4xl mx-auto px-6 text-center text-white space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold">-15% sur votre première commande</h2>
        <p className="text-emerald-100">Inscrivez-vous à notre newsletter et recevez votre code promo exclusif.</p>
        <div className="flex max-w-md mx-auto gap-2">
          <input className="flex-1 h-12 rounded-full px-5 bg-white/20 text-white placeholder:text-emerald-200 border border-white/30 focus:outline-none" placeholder="Votre email" />
          <Button className="bg-white text-emerald-700 hover:bg-emerald-50 h-12 px-6 rounded-full font-semibold">S'inscrire</Button>
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="bg-gray-50 border-t border-gray-200 py-12">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <EditableText as="p" value={displayName} onChange={edit("heroTitle")} editMode={editMode} className="text-xl font-bold text-emerald-600 mb-2" />
        <p className="text-sm text-gray-500">{city} — © 2026 Tous droits réservés — Propulsé par Grafton IA</p>
      </div>
    </footer>
  </div>
  );
};

export default EcommerceTemplate;
